import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';

import {
    BaseContainer,
    DialogConfig,
    DialogRef,
    ErrorResponse,
    MultiFormSelectOptions,
    SortDirection
} from '@stylehaven/components';
import { SavedQuery } from '../../../entities/saved-queries/saved-query.entity';
import { LoadSavedQueryDialogOutput } from '../../../entities/saved-queries/load-saved-query-dialog.entity';
import { SavedQueriesSandboxService } from '../../sandbox/saved-queries.sandbox';

@Component({
    standalone: false,
    selector: 'digihaven-load-saved-query-dialog',
    templateUrl: './load-saved-query-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadSavedQueryDialogComponent extends BaseContainer implements OnInit {
    readonly ErrorResponse = ErrorResponse;

    private searchTerm$: Subject<string> = new Subject<string>();

    /** The last batch of matches behind {@link savedQueriesFormSelectOptions$}, so `submit` can resolve the picked id back to a full query. */
    private lastSearchResults: SavedQuery[] = [];

    public savedQueriesFormSelectOptions$!: Observable<MultiFormSelectOptions>;

    public savedQueryFormGroup!: UntypedFormGroup;

    constructor(
        private readonly sandbox: SavedQueriesSandboxService,
        private readonly formBuilder: UntypedFormBuilder,
        public config: DialogConfig,
        public dialog: DialogRef<LoadSavedQueryDialogOutput>
    ) {
        super();
    }

    ngOnInit(): void {
        this.initForm();
        this.initGroupOptionsStream();
    }

    private initForm(): void {
        this.savedQueryFormGroup = this.formBuilder.group({
            querySearch: [, Validators.required]
        });
    }

    private initGroupOptionsStream(): void {
        this.savedQueriesFormSelectOptions$ = this.searchTerm$.pipe(
            distinctUntilChanged(),
            debounceTime(500),
            startWith(''),
            switchMap(searchTerm =>
                this.sandbox.getSavedQueries({
                    nrOfResults: 50,
                    name: `*${searchTerm}*`,
                    sort: 'Name',
                    direction: SortDirection.ASC
                })
            ),
            map(paginatedResults => paginatedResults.results),
            tap(savedQueries => (this.lastSearchResults = savedQueries)),
            map(
                (savedQueries): MultiFormSelectOptions => ({
                    isLiveData: true,
                    options: savedQueries.map(savedQuery => ({
                        label:
                            savedQuery.Groups.length > 0
                                ? `${savedQuery.Name} ${this.translateService.instant('components.saved-queries.panel.shared-suffix')}`
                                : savedQuery.Name,
                        value: savedQuery.Id
                    }))
                })
            )
        );
    }

    public onSearchTermChange(searchTerm: string): void {
        this.searchTerm$.next(searchTerm);
    }

    submit(): void {
        const id: string = this.querySearchControl.value[0];
        const query: SavedQuery | undefined = this.lastSearchResults.find(candidate => candidate.Id === id);

        if (!query) {
            console.error('LoadSavedQueryDialog', 'No value selected');
            return;
        }

        const output: LoadSavedQueryDialogOutput = {
            type: 'success',
            query
        };
        this.dialog.close(output);
    }

    cancel(): void {
        const output: LoadSavedQueryDialogOutput = { type: 'cancel' };
        this.dialog.close(output);
    }

    get querySearchControl(): UntypedFormControl {
        return this.savedQueryFormGroup.get('querySearch') as UntypedFormControl;
    }
}
